// GENERATED by scripts/generate-mcp-embedded-copies.mjs from convex/lib/flags_core.ts.
// DO NOT EDIT. Run `npm run mcp:embedded-copies` to regenerate.
// WP-488 Slice 3 (FEAT-1377) / DEC-701: Railway's isolated Docker build for
// packages/mcp-server cannot see convex/ — this file must physically exist here.
// See scripts/generate-mcp-embedded-copies.mjs for the full rationale.

// convex/lib/flags_core.ts — single source of truth for feature flags.
// (Underscore path: Convex module names disallow hyphens in path segments.)
// Chain: DEC-701 (pre-production flag simplification), STA-5 (amended),
//        BR-79 (kebab-case), BR-83 (amended), STD-35 (dead flag cleanup).
//
// This file lives in `convex/lib/` to keep the Convex tsconfig (which does
// not include `src/`) and the SvelteKit tsconfig in the same type graph.
// The MCP `prebuild` script copies this file into
// `packages/mcp-server/src/flags.ts` so that the MCP package sees the
// authoritative resolver without reaching across the monorepo boundary at
// runtime.

export type FlagCtx = {
	workspaceId?: string;
	clerkUserId?: string;
};

export type FlagRule =
	| boolean
	| {
			default: boolean;
			workspaceAllow?: readonly string[];
			workspaceDeny?: readonly string[];
			userAllow?: readonly string[];
			userDeny?: readonly string[];
			posthog?: boolean;
	  };

/**
 * Kill switch — resolves flag-first in every environment.
 * - Vite/browser: `VITE_FEATURE_KILL_SWITCH=true`
 * - Node (MCP/Convex action/test): `FEATURE_KILL_SWITCH=true`
 *
 * Runtime-safe: both checks are guarded with typeof + optional chaining so
 * missing globals (Convex runtime lacks `import.meta.env`; browsers lack
 * `process`) do not throw.
 */
function readViteEnvFlag(name: string): string | undefined {
	try {
		// `import.meta` exists everywhere but `.env` is Vite-only.
		const env = (import.meta as { env?: Record<string, string | undefined> }).env;
		return env?.[name];
	} catch {
		return undefined;
	}
}

function readProcessEnvFlag(name: string): string | undefined {
	try {
		if (typeof process !== 'undefined' && process.env) {
			return process.env[name];
		}
	} catch {
		// fall through
	}
	return undefined;
}

const KILL =
	readViteEnvFlag('VITE_FEATURE_KILL_SWITCH') === 'true' ||
	readProcessEnvFlag('FEATURE_KILL_SWITCH') === 'true';

/**
 * FEAT-1139 — Query budget kill-switch.
 * When true, `cappedQuery` bypasses tiered row caps and returns full unbounded
 * result sets while preserving the `_budget` telemetry envelope.
 * - Vite/browser: `VITE_PB_QUERY_BUDGET_KILL=true`
 * - Node (MCP/Convex action/test): `PB_QUERY_BUDGET_KILL=true`
 */
const QUERY_BUDGET_KILL =
	readViteEnvFlag('VITE_PB_QUERY_BUDGET_KILL') === 'true' ||
	readProcessEnvFlag('PB_QUERY_BUDGET_KILL') === 'true';

// Named IDs — dev + prod coexist per DEC-701. Convex deployment-prefix
// namespacing (p57... dev, mx78... prod) makes the WS sets disjoint at runtime.
// Clerk user IDs do NOT carry across envs: the dev/test instance (pk_test_) and
// prod instance (pk_live_) have separate user pools, so the same human resolves
// to different ids — USR.RANDY (prod) vs USR.RANDY_DEV (dev/preview). (TEN-2168)
export const WS = {
	RANDY_DEV_PRIMARY: 'p5796jhec0nhyp417ekrt8x4w181t41f',
	RANDY_DEV_SECONDARY: 'p5791wr86cj0thx2vxpqavj0mn82rssd',
	RANDY_PROD_PRIMARY: 'mx784e9jjdqhsk2r0098bpvtes84e792',
	RANDY_PROD_SECONDARY: 'mx74q0mkwn4r920q2837qk7dsx84pq60',
} as const;

export const USR = {
	/** Randy — production Clerk instance id (pk_live_); used by prod mvp-unlock allowlists. NOT the id local dev / Convex previews resolve to — those use the test instance (see RANDY_DEV). */
	RANDY: 'user_3C31UK8CWugoDXUj2RGu1HDudas',
	/** Randy — development/test Clerk instance id (pk_test_). Local dev + Convex previews authenticate via the test instance, so preview seeding (linkSeedOwner) and preview MVP unlock MUST use this id. Clerk dev/prod instances have separate user pools, so the ids differ. (DEC-1097 / TEN-2168) */
	RANDY_DEV: 'user_39r6ItzbAkdFY6XZyYTz79ALSYN',
	/** Niels — full MVP unlock in any workspace (Clerk JWT `sub` / `FlagCtx.clerkUserId`). */
	NIELS: 'user_3DOKRUaiY9KS3RKtC7shak3nB0N',
} as const;

/**
 * DEC-701 Phase 3: registry stays empty after Phase 2 because pre-production
 * products have no users to protect. Add flags here when genuinely needed —
 * shape is `boolean` for trivial toggles, or the FlagRule object for the
 * allowlist precedence described in DEC-701 Clause 3.
 *
 * WP-355 (MVP Surface Lock): five mvp-unlock-* flags gate Spine, Bridge, Rail,
 * Import, and Brain Chat. Default off; `workspaceAllow` lists Randy's dev/prod
 * workspaces; optional `userAllow` can grant the same unlocks to specific
 * Clerk user ids in any workspace (see USR.NIELS).
 */
export const FLAGS = {
	// WP-355 — MVP unlock: full Spine navigation (all modes + access patterns visible)
	'mvp-unlock-spine': {
		default: false,
		workspaceAllow: [
			WS.RANDY_DEV_PRIMARY,
			WS.RANDY_DEV_SECONDARY,
			WS.RANDY_PROD_PRIMARY,
			WS.RANDY_PROD_SECONDARY,
		],
		userAllow: [USR.NIELS, USR.RANDY, USR.RANDY_DEV],
	},
	// WP-355 — MVP unlock: Bridge full view (beyond locked /bridge dashboard)
	'mvp-unlock-bridge-full': {
		default: false,
		workspaceAllow: [
			WS.RANDY_DEV_PRIMARY,
			WS.RANDY_DEV_SECONDARY,
			WS.RANDY_PROD_PRIMARY,
			WS.RANDY_PROD_SECONDARY,
		],
		userAllow: [USR.NIELS, USR.RANDY, USR.RANDY_DEV],
	},
	// WP-355 — MVP unlock: Rail full context (beyond Rail context-only mode)
	'mvp-unlock-rail-full': {
		default: false,
		workspaceAllow: [
			WS.RANDY_DEV_PRIMARY,
			WS.RANDY_DEV_SECONDARY,
			WS.RANDY_PROD_PRIMARY,
			WS.RANDY_PROD_SECONDARY,
		],
		userAllow: [USR.NIELS, USR.RANDY, USR.RANDY_DEV],
	},
	// WP-355 — MVP unlock: Import surface enabled
	'mvp-unlock-import': {
		default: false,
		workspaceAllow: [
			WS.RANDY_DEV_PRIMARY,
			WS.RANDY_DEV_SECONDARY,
			WS.RANDY_PROD_PRIMARY,
			WS.RANDY_PROD_SECONDARY,
		],
		userAllow: [USR.NIELS, USR.RANDY, USR.RANDY_DEV],
	},
	// WP-355 — Brain Chat: off by default; same workspace + user allowlists as other mvp-unlock flags.
	'mvp-unlock-brain-chat': {
		default: false,
		workspaceAllow: [
			WS.RANDY_DEV_PRIMARY,
			WS.RANDY_DEV_SECONDARY,
			WS.RANDY_PROD_PRIMARY,
			WS.RANDY_PROD_SECONDARY,
		],
		userAllow: [USR.NIELS, USR.RANDY, USR.RANDY_DEV],
	},
	// FLAG: advanced-governance-modes — remove at GA per DEC-96.
	// DEC-97 (intent) + DEC-1138 (plumbing migration). Gates Consensus + Role-based
	// governance mode switching on the workspace settings page. OFF: "Coming soon"
	// + Notify Me (STA-11 compliant). ON: both modes selectable; Role additionally
	// gated by getGovernanceModeReadiness (the GA precondition, not the flag).
	// Cohort matches the mvp-unlock-* convention (workspace + user belt-and-suspenders),
	// extending DEC-97's strict workspace-scoping so Randy can dogfood from any workspace.
	// Niels excluded — governance switching has sharp UX (Role preconditions, auto-commit
	// loss on Consensus), so dogfood stays narrow until validated.
	'advanced-governance-modes': {
		default: false,
		workspaceAllow: [
			WS.RANDY_DEV_PRIMARY,
			WS.RANDY_DEV_SECONDARY,
			WS.RANDY_PROD_PRIMARY,
			WS.RANDY_PROD_SECONDARY,
		],
		userAllow: [USR.RANDY, USR.RANDY_DEV],
	},
	// WP-373 E3 — Routing Resolver SSOT killswitch.
	// When true, the new pure resolver short-circuits and mount sites execute the
	// legacy inline redirect/error logic verbatim. Default false: resolver runs.
	// FEATURE_KILL_SWITCH env beats this flag (precedence per .claude/rules/feature-flags.md).
	'routing-resolver-killswitch': false,
	// WP-384 S3: Child-domain creation affordance. OFF by default — "Coming soon" tooltip per DEC-97 / STA-11.
	'custom-authority-domains': false,
	// FEAT-1139 — Query budget kill-switch.
	// When true, `cappedQuery` reverts to raw `.collect()` behavior (no row caps)
	// while still populating `_budget` for WP-380 telemetry. Default false.
	'query-budget-kill': {
		default: QUERY_BUDGET_KILL,
	},
} as const satisfies Record<string, FlagRule>;

// Keys removed from FLAGS but not yet swept from all call sites (STD-35).
export const DEPRECATED_FLAG_KEYS: readonly string[] = [];

export type FlagKey = keyof typeof FLAGS;

type PosthogResolver = (flag: string) => boolean | undefined;

/**
 * @internal — exported for tests only. Do not consume from production code
 * (use `isFeatureEnabled` instead). Tests use this to exercise precedence
 * without mutating the real FLAGS registry.
 *
 * Precedence (DEC-701 Clause 3, top wins):
 *   kill switch > userAllow > userDeny > workspaceDeny > workspaceAllow
 *   > default > posthog fallback (opt-in)
 */
export function _resolve(
	flag: string,
	rule: FlagRule | undefined,
	ctx: FlagCtx,
	kill: boolean,
	posthog?: PosthogResolver,
): boolean {
	if (kill) return false;
	if (rule === undefined) return false;
	if (typeof rule === 'boolean') return rule;

	const { workspaceId, clerkUserId } = ctx;
	if (clerkUserId && rule.userAllow?.includes(clerkUserId)) return true;
	if (clerkUserId && rule.userDeny?.includes(clerkUserId)) return false;
	if (workspaceId && rule.workspaceDeny?.includes(workspaceId)) return false;
	if (workspaceId && rule.workspaceAllow?.includes(workspaceId)) return true;

	if (rule.posthog && posthog) {
		try {
			const hit = posthog(flag);
			if (typeof hit === 'boolean') return hit;
		} catch {
			// fall through to rule.default on any PostHog error
		}
	}
	return rule.default;
}

function defaultPosthog(flag: string): boolean | undefined {
	try {
		type PosthogLike = { isFeatureEnabled(k: string): boolean | undefined };
		const g = globalThis as unknown as { __posthog?: PosthogLike };
		return g.__posthog?.isFeatureEnabled(flag);
	} catch {
		return undefined;
	}
}

export function isFeatureEnabled(flag: FlagKey, ctx: FlagCtx = {}): boolean {
	const rule = (FLAGS as Record<string, FlagRule>)[flag as string];
	return _resolve(flag as string, rule, ctx, KILL, defaultPosthog);
}
